import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Toolbar,
  Divider,
  Button,
  Tooltip,
  useTheme,
  Switch,
  FormControlLabel,
  Menu,
  MenuItem,
  Badge
} from '@material-ui/core';
import {
  Code as CodeIcon,
  FormatIndentIncrease as IndentIcon,
  FormatIndentDecrease as OutdentIcon,
  FormatBold as BoldIcon,
  FormatItalic as ItalicIcon,
  Search as SearchIcon,
  FindReplace as ReplaceIcon,
  Brightness4 as DarkIcon,
  Brightness7 as LightIcon,
  Check as ValidIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Code as FormatIcon,
  Undo as UndoIcon,
  Redo as RedoIcon,
  FileCopy as CopyIcon,
  ContentPaste as PasteIcon
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import SearchReplace from './SearchReplace';
import AutoComplete, { useAutoComplete } from './AutoComplete';
import './styles.css';

const useStyles = makeStyles((theme) => ({
  editorContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '500px',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
    overflow: 'hidden',
    backgroundColor: theme.palette.background.paper
  },
  toolbar: {
    minHeight: '48px',
    backgroundColor: theme.palette.grey[50],
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(0, 1),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5)
  },
  editorWrapper: {
    display: 'flex',
    flex: 1,
    position: 'relative'
  },
  lineNumbers: {
    backgroundColor: theme.palette.grey[100],
    color: theme.palette.text.secondary,
    fontSize: '12px',
    fontFamily: 'monospace',
    padding: theme.spacing(1, 1),
    textAlign: 'right',
    minWidth: '50px',
    borderRight: `1px solid ${theme.palette.divider}`,
    userSelect: 'none',
    lineHeight: '18px'
  },
  codeArea: {
    flex: 1,
    fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
    fontSize: '13px',
    lineHeight: '18px',
    padding: theme.spacing(1),
    border: 'none',
    outline: 'none',
    resize: 'none',
    backgroundColor: 'transparent',
    color: theme.palette.text.primary,
    whiteSpace: 'pre',
    overflow: 'auto',
    tabSize: 2
  },
  highlightOverlay: {
    position: 'absolute',
    top: 0,
    left: '51px', // Largura dos números das linhas + border
    right: 0,
    bottom: 0,
    padding: theme.spacing(1),
    fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
    fontSize: '13px',
    lineHeight: '18px',
    pointerEvents: 'none',
    whiteSpace: 'pre',
    overflow: 'hidden',
    zIndex: 1,
    color: 'transparent'
  },
  statusBar: {
    backgroundColor: theme.palette.grey[100],
    borderTop: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(0.5, 2),
    fontSize: '12px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  // Syntax highlighting styles
  htmlTag: {
    color: '#3f51b5'
  },
  htmlAttribute: {
    color: '#4caf50'
  },
  htmlString: {
    color: '#ff9800'
  },
  htmlComment: {
    color: '#9e9e9e',
    fontStyle: 'italic'
  },
  cssProperty: {
    color: '#e91e63'
  },
  cssValue: {
    color: '#009688'
  },
  jsKeyword: {
    color: '#673ab7',
    fontWeight: 'bold'
  },
  searchHighlight: {
    backgroundColor: 'yellow',
    color: 'black'
  }
}));

const AdvancedHtmlEditor = ({ 
  value = '', 
  onChange, 
  height = '500px',
  placeholder = 'Digite seu HTML aqui...',
  showLineNumbers = true,
  enableSyntaxHighlight = true,
  theme: editorTheme = 'light'
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const textareaRef = useRef(null);
  const overlayRef = useRef(null);
  const editorRef = useRef(null);
  
  const [isDarkMode, setIsDarkMode] = useState(editorTheme === 'dark');
  const [showSearch, setShowSearch] = useState(false);
  const [cursorPosition, setCursorPosition] = useState({ line: 1, col: 1, index: 0 });
  const [validationErrors, setValidationErrors] = useState([]);
  const [history, setHistory] = useState([value]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [showAutoComplete, setShowAutoComplete] = useState(false);

  // Auto-complete hook
  const autoComplete = useAutoComplete(value, cursorPosition.index);

  // Calcular número de linhas
  const lines = value.split('\n');
  const lineCount = lines.length;

  // Syntax highlighting patterns
  const syntaxPatterns = {
    htmlTag: /<\/?[a-zA-Z][a-zA-Z0-9]*[^>]*>/g,
    htmlAttribute: /(\w+)(?==)/g,
    htmlString: /(["'])[^"']*\1/g,
    htmlComment: /<!--[\s\S]*?-->/g,
    cssProperty: /([a-zA-Z-]+)\s*:/g,
    cssValue: /:\s*([^;]+);/g,
    jsKeyword: /\b(function|var|let|const|if|else|for|while|return|class|extends)\b/g
  };

  // Aplicar syntax highlighting
  const applySyntaxHighlight = useCallback((text) => {
    if (!enableSyntaxHighlight) return text;

    // Escapar HTML para evitar corrupção
    let highlightedText = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
    
    // HTML Comments (primeiro para não interferir com outras regex)
    highlightedText = highlightedText.replace(
      /&lt;!--[\s\S]*?--&gt;/g,
      '<span class="html-comment">$&</span>'
    );

    // HTML Tags
    highlightedText = highlightedText.replace(
      /&lt;\/?[a-zA-Z][a-zA-Z0-9]*[^&gt;]*&gt;/g,
      '<span class="html-tag">$&</span>'
    );

    // HTML Strings (entre aspas)
    highlightedText = highlightedText.replace(
      /(&quot;[^&quot;]*&quot;|&#039;[^&#039;]*&#039;)/g,
      '<span class="html-string">$&</span>'
    );

    // CSS Properties
    highlightedText = highlightedText.replace(
      /([a-zA-Z-]+)\s*:/g,
      '<span class="css-property">$1</span>:'
    );

    // JavaScript Keywords
    highlightedText = highlightedText.replace(
      /\b(function|var|let|const|if|else|for|while|return|class|extends)\b/g,
      '<span class="js-keyword">$&</span>'
    );

    return highlightedText;
  }, [enableSyntaxHighlight]);

  // Validação básica de HTML
  const validateHtml = useCallback((html) => {
    const errors = [];
    
    // Verificar tags não fechadas
    const openTags = html.match(/<([a-zA-Z][a-zA-Z0-9]*)[^/>]*[^/]>/g) || [];
    const closeTags = html.match(/<\/([a-zA-Z][a-zA-Z0-9]*)>/g) || [];
    
    const openTagNames = openTags.map(tag => tag.match(/<([a-zA-Z][a-zA-Z0-9]*)/)[1].toLowerCase());
    const closeTagNames = closeTags.map(tag => tag.match(/<\/([a-zA-Z][a-zA-Z0-9]*)/)[1].toLowerCase());
    
    // Tags auto-fecháveis
    const selfClosingTags = ['img', 'br', 'hr', 'input', 'meta', 'link', 'area', 'base', 'col', 'embed', 'source', 'track', 'wbr'];
    
    openTagNames.forEach(tagName => {
      if (!selfClosingTags.includes(tagName)) {
        const openCount = openTagNames.filter(t => t === tagName).length;
        const closeCount = closeTagNames.filter(t => t === tagName).length;
        
        if (openCount > closeCount) {
          errors.push(`Tag '${tagName}' não foi fechada`);
        }
      }
    });

    setValidationErrors(errors);
    return errors.length === 0;
  }, []);

  // Auto-indentação
  const autoIndent = useCallback((text) => {
    const lines = text.split('\n');
    let indentLevel = 0;
    const indentedLines = [];

    lines.forEach(line => {
      const trimmedLine = line.trim();
      
      // Diminuir indentação para tags de fechamento
      if (trimmedLine.startsWith('</') && indentLevel > 0) {
        indentLevel--;
      }

      // Aplicar indentação
      indentedLines.push('  '.repeat(indentLevel) + trimmedLine);

      // Aumentar indentação para tags de abertura (não auto-fecháveis)
      if (trimmedLine.match(/<[a-zA-Z][a-zA-Z0-9]*[^/>]*[^/]>/) && 
          !trimmedLine.includes('</')) {
        indentLevel++;
      }
    });

    return indentedLines.join('\n');
  }, []);

  // Formatação automática
  const formatCode = useCallback(() => {
    const formattedText = autoIndent(value);
    onChange(formattedText);
    addToHistory(formattedText);
  }, [value, onChange, autoIndent]);

  // Histórico de mudanças
  const addToHistory = useCallback((newValue) => {
    if (newValue !== history[historyIndex]) {
      const newHistory = history.slice(0, historyIndex + 1);
      newHistory.push(newValue);
      setHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
    }
  }, [history, historyIndex]);

  // Desfazer
  const undo = useCallback(() => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      onChange(history[newIndex]);
    }
  }, [historyIndex, history, onChange]);

  // Refazer
  const redo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      onChange(history[newIndex]);
    }
  }, [historyIndex, history, onChange]);

  // Atualizar posição do cursor
  const updateCursorPosition = useCallback(() => {
    if (textareaRef.current) {
      const textarea = textareaRef.current;
      const cursorPos = textarea.selectionStart;
      const textBeforeCursor = value.substring(0, cursorPos);
      const lines = textBeforeCursor.split('\n');
      const line = lines.length;
      const col = lines[lines.length - 1].length + 1;
      setCursorPosition({ line, col, index: cursorPos });

      // Mostrar auto-complete se houver contexto
      if (autoComplete.context && autoComplete.searchTerm) {
        setShowAutoComplete(true);
        // Calcular posição do dropdown
        const rect = textarea.getBoundingClientRect();
        const lineHeight = 18;
        const charWidth = 7.2; // Aproximado para fontes monospace
        autoComplete.setPosition({
          left: rect.left + (col - autoComplete.searchTerm.length) * charWidth,
          top: rect.top + (line - 1) * lineHeight + lineHeight
        });
      } else {
        setShowAutoComplete(false);
      }
    }
  }, [value, autoComplete]);

  // Manipular teclas especiais
  const handleKeyDown = useCallback((event) => {
    const { key, ctrlKey, metaKey, shiftKey } = event;
    const isCmd = ctrlKey || metaKey;

    // Tab para indentação
    if (key === 'Tab') {
      event.preventDefault();
      const textarea = event.target;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const selectedText = value.substring(start, end);
      
      if (shiftKey) {
        // Shift+Tab: remover indentação
        const newText = selectedText.replace(/^  /gm, '');
        const newValue = value.substring(0, start) + newText + value.substring(end);
        onChange(newValue);
      } else {
        // Tab: adicionar indentação
        if (selectedText.includes('\n')) {
          // Múltiplas linhas
          const newText = selectedText.replace(/^/gm, '  ');
          const newValue = value.substring(0, start) + newText + value.substring(end);
          onChange(newValue);
        } else {
          // Linha única
          const newValue = value.substring(0, start) + '  ' + value.substring(start);
          onChange(newValue);
          setTimeout(() => {
            textarea.setSelectionRange(start + 2, start + 2);
          }, 0);
        }
      }
      return;
    }

    // Atalhos
    if (isCmd) {
      switch (key) {
        case 'z':
          if (shiftKey) {
            redo();
          } else {
            undo();
          }
          event.preventDefault();
          break;
        case 'f':
          setShowSearch(!showSearch);
          event.preventDefault();
          break;
        case 's':
          // Salvar pode ser implementado aqui
          event.preventDefault();
          break;
        case 'i':
          formatCode();
          event.preventDefault();
          break;
      }
    }

    // Auto-close de tags
    if (key === '>') {
      const textarea = event.target;
      const cursorPos = textarea.selectionStart;
      const textBeforeCursor = value.substring(0, cursorPos);
      const tagMatch = textBeforeCursor.match(/<([a-zA-Z][a-zA-Z0-9]*)[^>]*$/);
      
      if (tagMatch && !textBeforeCursor.endsWith('/') && !textBeforeCursor.includes('</')) {
        const tagName = tagMatch[1];
        const selfClosingTags = ['img', 'br', 'hr', 'input', 'meta', 'link', 'area', 'base', 'col', 'embed', 'source', 'track', 'wbr'];
        
        if (!selfClosingTags.includes(tagName.toLowerCase())) {
          event.preventDefault();
          const newValue = value.substring(0, cursorPos) + `></${tagName}>` + value.substring(cursorPos);
          onChange(newValue);
          setTimeout(() => {
            textarea.setSelectionRange(cursorPos + 1, cursorPos + 1);
          }, 0);
        }
      }
    }
  }, [value, onChange, showSearch, undo, redo, formatCode]);

  // Efeito para validação
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      validateHtml(value);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [value, validateHtml]);

  // Efeito para posição do cursor
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.addEventListener('selectionchange', updateCursorPosition);
      return () => {
        if (textareaRef.current) {
          textareaRef.current.removeEventListener('selectionchange', updateCursorPosition);
        }
      };
    }
  }, [updateCursorPosition]);

  const handleTextChange = (event) => {
    const newValue = event.target.value;
    onChange(newValue);
    addToHistory(newValue);
    updateCursorPosition();
    
    // Sincronizar scroll entre textarea e overlay
    if (overlayRef.current && event.target) {
      overlayRef.current.scrollTop = event.target.scrollTop;
      overlayRef.current.scrollLeft = event.target.scrollLeft;
    }
  };

  // Lidar com seleção do auto-complete
  const handleAutoCompleteSelect = useCallback((suggestion) => {
    if (!textareaRef.current) return;

    const textarea = textareaRef.current;
    const cursorPos = textarea.selectionStart;
    const textBeforeCursor = value.substring(0, cursorPos);
    const textAfterCursor = value.substring(cursorPos);

    // Remover o texto que foi digitado para busca
    const searchLength = autoComplete.searchTerm.length;
    const beforeSearch = textBeforeCursor.substring(0, textBeforeCursor.length - searchLength);
    
    // Inserir a sugestão
    const newValue = beforeSearch + suggestion.completion + textAfterCursor;
    onChange(newValue);
    addToHistory(newValue);

    // Posicionar cursor após a inserção
    setTimeout(() => {
      const newCursorPos = beforeSearch.length + suggestion.completion.length;
      // Para tags, posicionar cursor entre as tags
      if (suggestion.type === 'tag' && !suggestion.completion.includes('/>')) {
        const closingTagPos = suggestion.completion.indexOf('><');
        if (closingTagPos > -1) {
          textarea.setSelectionRange(
            beforeSearch.length + closingTagPos + 1,
            beforeSearch.length + closingTagPos + 1
          );
        }
      } else {
        textarea.setSelectionRange(newCursorPos, newCursorPos);
      }
      textarea.focus();
    }, 0);

    setShowAutoComplete(false);
  }, [value, autoComplete.searchTerm, onChange, addToHistory]);

  return (
    <Paper className={classes.editorContainer} style={{ height }}>
      {/* Toolbar */}
      <Toolbar className={classes.toolbar} variant="dense">
        <Tooltip title="Desfazer (Ctrl+Z)">
          <IconButton 
            size="small" 
            onClick={undo}
            disabled={historyIndex === 0}
          >
            <UndoIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title="Refazer (Ctrl+Shift+Z)">
          <IconButton 
            size="small" 
            onClick={redo}
            disabled={historyIndex >= history.length - 1}
          >
            <RedoIcon />
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem />

        <Tooltip title="Formatar Código (Ctrl+I)">
          <IconButton size="small" onClick={formatCode}>
            <FormatIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title="Buscar (Ctrl+F)">
          <IconButton 
            size="small" 
            onClick={() => setShowSearch(!showSearch)}
          >
            <SearchIcon />
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem />

        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={enableSyntaxHighlight}
              onChange={(e) => {
                // Esta função precisa ser implementada no state
                console.log('Toggle syntax highlighting:', e.target.checked);
              }}
            />
          }
          label="Highlight"
        />

        <div style={{ flexGrow: 1 }} />

        {/* Status de validação */}
        {validationErrors.length > 0 ? (
          <Tooltip title={validationErrors.join(', ')}>
            <Badge badgeContent={validationErrors.length} color="error">
              <ErrorIcon color="error" />
            </Badge>
          </Tooltip>
        ) : (
          <Tooltip title="HTML válido">
            <ValidIcon color="primary" />
          </Tooltip>
        )}
      </Toolbar>

      {/* Área do editor */}
      <div className={classes.editorWrapper}>
        {showLineNumbers && (
          <div className={classes.lineNumbers}>
            {Array.from({ length: lineCount }, (_, i) => (
              <div key={i + 1}>{i + 1}</div>
            ))}
          </div>
        )}

        <textarea
          ref={textareaRef}
          className={classes.codeArea}
          value={value}
          onChange={handleTextChange}
          onKeyDown={handleKeyDown}
          onMouseUp={updateCursorPosition}
          onScroll={(e) => {
            if (overlayRef.current) {
              overlayRef.current.scrollTop = e.target.scrollTop;
              overlayRef.current.scrollLeft = e.target.scrollLeft;
            }
          }}
          placeholder={placeholder}
          spellCheck={false}
          style={{ zIndex: 2, position: 'relative', backgroundColor: 'transparent' }}
        />

        {enableSyntaxHighlight && (
          <div
            ref={overlayRef}
            className={classes.highlightOverlay}
            dangerouslySetInnerHTML={{
              __html: applySyntaxHighlight(value)
            }}
          />
        )}
      </div>

      {/* Status Bar */}
      <div className={classes.statusBar}>
        <Typography variant="caption">
          Linha {cursorPosition.line}, Coluna {cursorPosition.col}
        </Typography>
        <Typography variant="caption">
          {value.length} caracteres, {lineCount} linhas
        </Typography>
        {validationErrors.length > 0 && (
          <Typography variant="caption" color="error">
            {validationErrors.length} erro(s)
          </Typography>
        )}
      </div>

      {/* Componente de busca e substituição */}
      <SearchReplace
        isOpen={showSearch}
        onClose={() => setShowSearch(false)}
        value={value}
        onChange={onChange}
        textareaRef={textareaRef}
      />

      {/* Componente de auto-complete */}
      <AutoComplete
        isVisible={showAutoComplete}
        position={autoComplete.position}
        context={autoComplete.context}
        searchTerm={autoComplete.searchTerm}
        onSelect={handleAutoCompleteSelect}
        onClose={() => setShowAutoComplete(false)}
      />
    </Paper>
  );
};

export default AdvancedHtmlEditor;